/**
 * Inline live tail for a background SDK task.
 *
 * This is the compact version of the old full-pane task output
 * viewer: it tails the workspace server's per-task output endpoint
 * from inside the originating tool row, keeps the terminal pinned to
 * the newest output until the user scrolls away, and leaves chat plus
 * compose chrome mounted around it.
 */

import * as React from 'react';
import { Spinner } from '@/components/ui/spinner';
import { fetchTaskOutput, type TaskOutputSlice } from '@/lib/agent/api';
import type { ActiveTask } from '@/lib/agent/reducer';
import { cn } from '@/lib/utils/cn';
import { normalizeTerminalOutput } from './helpers';

const DEFAULT_POLL_INTERVAL_MS = 1000;
const FOLLOW_THRESHOLD_PX = 32;
const TASK_OUTPUT_CHUNK_BYTES = 256 * 1024;
export const TASK_OUTPUT_RETAIN_CHARS = 512 * 1024;

export type TaskOutputReader = (
  cwd: string,
  sessionId: string,
  taskId: string,
  opts: { offset?: number; maxBytes?: number; tail?: boolean }
) => Promise<TaskOutputSlice>;

export function appendTaskOutputTail(previous: string, next: string, retainChars = TASK_OUTPUT_RETAIN_CHARS): string {
  if (retainChars <= 0) return '';
  if (!next) return previous.length > retainChars ? previous.slice(-retainChars) : previous;
  if (next.length >= retainChars) return next.slice(-retainChars);
  const previousBudget = retainChars - next.length;
  return `${previous.slice(-previousBudget)}${next}`;
}

export interface TaskTailViewProps {
  task: ActiveTask;
  cwd: string | null;
  sessionId: string | null;
  isRunning: boolean;
  command?: string | null;
  outputReader?: TaskOutputReader;
  pollIntervalMs?: number;
  className?: string;
}

export function TaskTailView({
  task,
  cwd,
  sessionId,
  isRunning,
  command,
  outputReader = fetchTaskOutput,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  className,
}: TaskTailViewProps): React.ReactElement {
  const taskId = task.taskId;
  const commandLabel = command?.trim();
  const [content, setContent] = React.useState('');
  const [exists, setExists] = React.useState<boolean | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // The output file is a terminal stream, so keep following unless
  // the user intentionally scrolls away from the tail.
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const followRef = React.useRef(true);
  const offsetRef = React.useRef(0);
  const isRunningRef = React.useRef(isRunning);
  // Always collapse `\r` repaints into the visible transcript so
  // tqdm-style progress bars stay on a single line both while the
  // task is running AND after it finishes. We used to skip this on
  // completion, which made finished output suddenly sprawl every
  // intermediate progress repaint onto its own line.
  const displayContent = React.useMemo(() => normalizeTerminalOutput(content), [content]);

  React.useEffect(() => {
    isRunningRef.current = isRunning;
  }, [isRunning]);

  // Reset state on task switch so an old task's tail never flashes
  // under a freshly selected row.
  // biome-ignore lint/correctness/useExhaustiveDependencies: taskId is the trigger
  React.useEffect(() => {
    setContent('');
    offsetRef.current = 0;
    setExists(null);
    setError(null);
    followRef.current = true;
  }, [taskId]);

  React.useEffect(() => {
    if (!cwd || !sessionId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      let latestSlice: TaskOutputSlice | null = null;
      try {
        const slice = await outputReader(cwd, sessionId, taskId, {
          offset: offsetRef.current,
          maxBytes: TASK_OUTPUT_CHUNK_BYTES,
          tail: offsetRef.current === 0,
        });
        latestSlice = slice;
        if (cancelled) return;
        setExists(slice.exists);
        if (slice.content.length > 0) {
          setContent((prev) => appendTaskOutputTail(prev, slice.content));
        }
        offsetRef.current = slice.end_offset;
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not read task output');
      }
      if (cancelled) return;
      const shouldKeepReading = isRunningRef.current || (latestSlice?.exists === true && !latestSlice.eof);
      if (!shouldKeepReading) return;
      timer = setTimeout(poll, isRunningRef.current ? pollIntervalMs : 0);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [cwd, sessionId, taskId, outputReader, pollIntervalMs]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: content is the trigger
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el || !followRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [content]);

  function handleScroll(event: React.UIEvent<HTMLDivElement>): void {
    const el = event.currentTarget;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    followRef.current = distance < FOLLOW_THRESHOLD_PX;
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border border-gray-200/70 bg-white dark:border-white/10 dark:bg-[#0a0d10]',
        className
      )}
    >
      {commandLabel && (
        <div className="flex min-w-0 items-center border-gray-100 border-b px-2.5 py-1.5 dark:border-white/[0.06]">
          <span
            className="flex min-w-0 items-center gap-1 truncate font-mono text-[11.5px] text-gray-500 leading-snug dark:text-gray-400"
            title={commandLabel}
          >
            <span className="select-none text-gray-400 dark:text-gray-500" aria-hidden>
              $
            </span>
            <span className="min-w-0 select-text truncate">{commandLabel}</span>
          </span>
        </div>
      )}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className={cn('agent-scroll max-h-28 min-h-[4.5rem] overflow-auto px-2.5 py-1.5', '[scrollbar-gutter:stable]')}
      >
        <pre
          className={cn(
            'select-text whitespace-pre-wrap break-words font-mono text-[11.5px] leading-relaxed',
            'text-gray-800 dark:text-gray-200'
          )}
        >
          {displayContent || (
            <span className="select-none text-gray-400 italic dark:text-gray-500">
              {error ? (
                <>Could not read task output: {error}</>
              ) : exists === false ? (
                <>No output yet - task has not written anything</>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <Spinner size="size-3" />
                  Waiting for output...
                </span>
              )}
            </span>
          )}
        </pre>
      </div>
    </div>
  );
}
